import { CentricTypography as Typography } from '@core/component/CentricTypography'
import React from 'react'
import classNames from 'classnames'
import { Chip, Grid, makeStyles } from '@material-ui/core'
import { Tag } from '@components/UI-Components/Tag'
import { AttributeCaseTypes, AttributeDisplayTypes } from '../product-settings-constants'

const useStyles = makeStyles(theme => ({
  rendererRoot: {
    width: '100%',
    flexDirection: 'column',
    marginTop: 5,
    '&:first-child': {
      marginTop: 0
    }
  },
  withLabelRoot: {
    marginTop: 10
  },
  compressedCardInfo: {
    marginTop: 4,
    '&:first-child': {
      marginTop: 0
    }
  },
  compressedCardInfoWithoutLabel: {
    marginTop: 0
  },
  productTitleText: {
    width: '95%',
    marginTop: 2
  },
  clampTwoLinesText: {
    width: '95%',
    minHeight: '2.5rem',
    maxHeight: '2.5rem',
    overflow: 'hidden',
    whiteSpace: 'normal',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    '-webkit-line-clamp': 2,
    '-webkit-box-orient': 'vertical',
    '-o-text-overflow': 'ellipsis',
    '& .clampTwoLinesTextChild': {
      width: '100%',
      overflow: 'hidden',
      whiteSpace: 'normal',
      textOverflow: 'ellipsis',
      display: '-webkit-box',
      '-webkit-line-clamp': 2,
      '-webkit-box-orient': 'vertical',
      '-o-text-overflow': 'ellipsis'
    },
    '&.regularTextClampOverride': {
      minHeight: '2.2rem',
      maxHeight: '2.2rem'
    }
  },
  productBoldText: {
    marginTop: 0,
    marginBottom: 0,
    width: '95%'
  },
  infoLabel: {
    color: '#bcbcbc',
    width: '95%'
  },
  textLeftAlign: {
    textAlign: 'left'
  },
  textDefaultAlignment: {
    textAlign: 'center'
  },
  ellipsisText: {
    width: '95%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    '& h6, & span': {
      overflow: 'inherit',
      textOverflow: 'inherit',
      whiteSpace: 'inherit'
    }
  },
  productCardInfoText: {
    width: '95%'
  },
  chip: {
    margin: '0px 5px 5px 0px',
    borderRadius: '16px',
    backgroundColor: 'rgba(0, 0, 0, 0.05)'
  },
  chipRoot: {
    height: '28px',
    maxWidth: '100%',
    margin: '2px'
  },
  label: {
    paddingLeft: '6px',
    display: 'flex',
    alignItems: 'center'
  },
  tag: {
    fontWeight: 'bold'
  },
  chipDash: {
    height: 30,
    lineHeight: 2
  },
  [AttributeCaseTypes.UPPRECASE]: {
    textTransform: 'uppercase'
  },
  [AttributeCaseTypes.LOWERCASE]: {
    textTransform: 'lowercase'
  },
  [AttributeCaseTypes.TITLECASE]: {
    textTransform: 'capitalize'
  },
  [AttributeCaseTypes.NONE]: {
    textTransform: 'none'
  },
  smallBoldTitleFont: {
    lineHeight: 'unset',
    fontSize: '0.75rem'
  },
  smallBoldFont: {
    lineHeight: 'unset',
    fontSize: '0.625rem'
  },
  smallRegularTextFont: {
    lineHeight: 'unset',
    fontSize: '0.625rem'
  },
  noLineHeight: {
    lineHeight: 'unset'
  }
}))

export const getCaseFormattedValue = (value, caseType) => {
  switch (caseType) {
    case AttributeCaseTypes.UPPRECASE:
    case AttributeCaseTypes.LOWERCASE:
    case AttributeCaseTypes.TITLECASE:
      return value.toLowerCase()
    default:
      return value
  }
}

export interface AttributeRendererProps {
  value?: any
  showLabel?: boolean
  hasDoubleSpace?: boolean
  caseType?: string
  label?: string
  isLeftAlined?: boolean
  isCompressedCard?: boolean
}

export const LabelRenderer: React.FunctionComponent<AttributeRendererProps> = ({
  label,
  isLeftAlined,
  isCompressedCard
}) => {
  const classes = useStyles({})
  return (
    <Grid
      className={classNames(
        classes.infoLabel,
        classes.ellipsisText,
        isLeftAlined ? classes.textLeftAlign : classes.textDefaultAlignment
      )}
    >
      <Typography
        variant={'overlineLightGrey'}
        className={classNames({
          [classes.noLineHeight]: isCompressedCard
        })}
      >
        {label.toUpperCase()}
      </Typography>
    </Grid>
  )
}

export const BoldTitleTextRenderer: React.FunctionComponent<AttributeRendererProps> = ({
  value,
  showLabel,
  hasDoubleSpace,
  caseType,
  label = '',
  isLeftAlined,
  isCompressedCard
}) => {
  // 86px = 40px + 20px + 10px/5px margin
  const hasLabel = showLabel
  const classes = useStyles()

  return (
    <Grid
      container
      className={classNames(classes.rendererRoot, {
        [classes.withLabelRoot]: hasLabel,
        [classes.compressedCardInfo]: isCompressedCard,
        [classes.compressedCardInfoWithoutLabel]: isCompressedCard && !hasLabel
      })}
    >
      {hasLabel && (
        <LabelRenderer
          label={label}
          isLeftAlined={isLeftAlined}
          isCompressedCard={isCompressedCard}
        />
      )}
      <div
        className={classNames(classes.productTitleText, {
          [classes.ellipsisText]: !hasDoubleSpace,
          [classes.clampTwoLinesText]: hasDoubleSpace
        })}
      >
        <Typography
          variant={'h6'}
          align={isLeftAlined ? 'left' : 'center'}
          className={classNames({
            [classes[caseType]]: caseType,
            [classes.smallBoldTitleFont]: isCompressedCard,
            clampTwoLinesTextChild: hasDoubleSpace
          })}
        >
          {getCaseFormattedValue(value, caseType)}
        </Typography>
      </div>
    </Grid>
  )
}

export const RegularTextRenderer: React.FunctionComponent<AttributeRendererProps> = ({
  value,
  showLabel,
  hasDoubleSpace,
  caseType,
  label = '',
  isLeftAlined,
  isCompressedCard = false
}) => {
  // 30px = 20px + 10px/5px margin
  const hasLabel = showLabel
  const classes = useStyles()
  return (
    <Grid
      container
      className={classNames(classes.rendererRoot, {
        [classes.withLabelRoot]: hasLabel,
        [classes.compressedCardInfo]: isCompressedCard,
        [classes.compressedCardInfoWithoutLabel]: isCompressedCard && !hasLabel
      })}
    >
      {hasLabel && (
        <LabelRenderer
          label={label}
          isLeftAlined={isLeftAlined}
          isCompressedCard={isCompressedCard}
        />
      )}
      <div
        className={classNames(classes.productCardInfoText, {
          [classes.ellipsisText]: !hasDoubleSpace,
          [classes.clampTwoLinesText]: hasDoubleSpace,
          [classes.textDefaultAlignment]: !isLeftAlined,
          [classes.textLeftAlign]: isLeftAlined,
          regularTextClampOverride: hasDoubleSpace
        })}
      >
        <Typography
          variant={'captionDarkGrey'}
          noWrap
          align={isLeftAlined ? 'left' : 'center'}
          className={classNames({
            [classes[caseType]]: caseType,
            [classes.smallRegularTextFont]: isCompressedCard,
            clampTwoLinesTextChild: hasDoubleSpace
          })}
        >
          {getCaseFormattedValue(value, caseType)}
        </Typography>
      </div>
    </Grid>
  )
}

export const BoldTextRenderer: React.FunctionComponent<AttributeRendererProps> = ({
  value,
  showLabel,
  hasDoubleSpace,
  caseType,
  label = '',
  isLeftAlined,
  isCompressedCard = false
}) => {
  // 52px = 20px + 20px + 10px/5px margin
  const hasLabel = showLabel
  const classes = useStyles()

  return (
    <Grid
      container
      className={classNames(classes.rendererRoot, {
        [classes.withLabelRoot]: hasLabel,
        [classes.compressedCardInfo]: isCompressedCard,
        [classes.compressedCardInfoWithoutLabel]: isCompressedCard && !hasLabel
      })}
    >
      {hasLabel && (
        <LabelRenderer
          label={label}
          isLeftAlined={isLeftAlined}
          isCompressedCard={isCompressedCard}
        />
      )}
      <div
        className={classNames(classes.productBoldText, {
          [classes.ellipsisText]: !hasDoubleSpace,
          [classes.clampTwoLinesText]: hasDoubleSpace,
          [classes.textLeftAlign]: isLeftAlined,
          [classes.textDefaultAlignment]: !isLeftAlined
        })}
      >
        <Typography
          variant={'h6'}
          noWrap
          align={isLeftAlined ? 'left' : 'center'}
          className={classNames({
            [classes[caseType]]: caseType,
            [classes.smallBoldFont]: isCompressedCard,
            clampTwoLinesTextChild: hasDoubleSpace
          })}
        >
          {getCaseFormattedValue(value, caseType)}
        </Typography>
      </div>
    </Grid>
  )
}

export const ChipRenderer: React.FunctionComponent<AttributeRendererProps> = ({
  value: chipsData = [],
  showLabel,
  label = '',
  isLeftAlined,
  isCompressedCard = false
}) => {
  const hasLabel = showLabel
  const classes = useStyles()

  return (
    <Grid
      container
      className={classNames(classes.rendererRoot, {
        [classes.withLabelRoot]: hasLabel,
        [classes.compressedCardInfo]: isCompressedCard,
        [classes.compressedCardInfoWithoutLabel]: isCompressedCard && !hasLabel
      })}
    >
      {hasLabel && <LabelRenderer label={label} isLeftAlined={isLeftAlined} />}
      <div
        className={classNames(
          classes.productBoldText,
          isLeftAlined ? classes.textLeftAlign : classes.textDefaultAlignment
        )}
      >
        {Array.isArray(chipsData) ? (
          chipsData.map(item => (
            <Chip
              key={item.title}
              classes={{
                label: classes.label,
                root: classes.chipRoot
              }}
              className={classes.chip}
              label={
                <Tag
                  name={item.title}
                  tagStyle={classes.tag}
                  color={item.color}
                  clusterStatus={item.clusterStatus}
                  customeToolTipText={item.tooltip}
                />
              }
            />
          ))
        ) : (
          <Typography
            variant={'h6'}
            noWrap
            align={isLeftAlined ? 'left' : 'center'}
            className={classes.chipDash}
          >
            {chipsData}
          </Typography>
        )}
      </div>
    </Grid>
  )
}

export const FieldTypeElementMap = {
  [AttributeDisplayTypes.BOLD_TITLE_TEXT]: BoldTitleTextRenderer,
  [AttributeDisplayTypes.REGULAR_BODY_TEXT]: RegularTextRenderer,
  [AttributeDisplayTypes.BOLD_TEXT]: BoldTextRenderer,
  [AttributeDisplayTypes.CHIP]: ChipRenderer
}
